import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import Device from './Device';
import EnterpriseDeviceCluster from './EnterpriseDeviceCluster';
import { getNodeDetails } from '../services/nodeApi';
import '../styles/asset-details.css';

/**
 * Asset Details Page
 * Displays detailed information about a specific RWA device/node
 */
const AssetDetailsPage = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const { wallet } = useWallet();
  const [asset, setAsset] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('overview');

  // Fetch asset details when component mounts
  useEffect(() => {
    if (wallet.connected) {
      fetchAssetDetails();
    } else {
      setIsLoading(false);
    }
  }, [wallet, assetId]);

  // Fetch asset details from API
  const fetchAssetDetails = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getNodeDetails(
        assetId,
        wallet.address,
        wallet.network === 'solana' ? 3 : (wallet.network === 'bsc' ? 2 : 1), // Convert network to numeric ID
        wallet.type
      );
      
      if (response.success && response.data) {
        // Transform API response to asset format
        const assetData = transformNodeToAsset(response.data);
        setAsset(assetData);
      } else {
        setError(response.message || 'Failed to fetch asset details');
      }
    } catch (error) {
      console.error('Error fetching asset details:', error);
      setError('Failed to load asset details. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Transform node data from API to asset format
  const transformNodeToAsset = (nodeData) => {
    return {
      id: nodeData.id || nodeData.node_id,
      name: nodeData.name || `${nodeData.tier_name || 'Standard'} Device`,
      serialNumber: nodeData.reference_code || nodeData.serial_number || nodeData.id,
      type: nodeData.tier_id?.toLowerCase() || 'standard',
      tier: nodeData.tier_name || 'Standard',
      status: nodeData.status || 'active',
      value: nodeData.value || nodeData.initial_value || 5000,
      currentApy: nodeData.current_apy || nodeData.apy || 10,
      miningFactor: nodeData.mining_factor || 1,
      totalRewards: nodeData.total_rewards || 0,
      dailyRewards: nodeData.daily_rewards || 0,
      activationDate: nodeData.activation_date || nodeData.created_at,
      expiryDate: nodeData.expiry_date,
      remainingDays: nodeData.remaining_days,
      networkPosition: nodeData.network_position || 'Standard',
      owner: {
        walletAddress: nodeData.wallet_address || wallet.address,
        name: nodeData.owner_name
      },
      stats: nodeData.stats || {},
      // Add additional fields as needed
    };
  };

  // Format currency for display
  const formatCurrency = (value) => {
    if (!value) return '$0.00';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long', 
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  // Format blockchain address for display
  const formatAddress = (address) => {
    if (!address) return 'N/A';
    if (address.length <= 14) return address;
    
    return `${address.slice(0, 6)}...${address.slice(-6)}`;
  };

  // Render wallet disconnected state
  const renderWalletDisconnected = () => (
    <div className="asset-details-empty">
      <div className="empty-state-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 7L12 3L4 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M4 7V17L12 21L20 17V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11L4 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <h3>Wallet Not Connected</h3>
      <p>Connect your wallet to view asset details.</p>
      <div className="asset-details-actions">
        <button 
          className="btn btn-secondary" 
          onClick={() => navigate('/portfolio')}
        >
          Go to Portfolio
        </button>
        <button 
          className="btn" 
          onClick={() => wallet.openWalletModal ? wallet.openWalletModal() : null}
        >
          Connect Wallet
        </button>
      </div>
    </div>
  );

  // Render the overview tab
  const renderOverviewTab = () => (
    <div className="asset-overview-tab">
      <div className="asset-info-grid">
        <div className="info-item">
          <div className="info-label">Status</div>
          <div className="info-value status">{asset.status}</div>
        </div>
        
        <div className="info-item">
          <div className="info-label">Type</div>
          <div className="info-value">{asset.tier}</div>
        </div>
        
        <div className="info-item">
          <div className="info-label">Serial Number</div>
          <div className="info-value">{asset.serialNumber}</div>
        </div>
        
        <div className="info-item">
          <div className="info-label">Value</div>
          <div className="info-value">{formatCurrency(asset.value)}</div>
        </div>
        
        <div className="info-item">
          <div className="info-label">Current APY</div>
          <div className="info-value">{asset.currentApy}%</div>
        </div>
        
        <div className="info-item">
          <div className="info-label">Mining Factor</div>
          <div className="info-value">{asset.miningFactor}x</div>
        </div>
        
        <div className="info-item">
          <div className="info-label">Activation Date</div>
          <div className="info-value">{formatDate(asset.activationDate)}</div>
        </div>
        
        {asset.expiryDate && (
          <div className="info-item">
            <div className="info-label">Expiry Date</div>
            <div className="info-value">{formatDate(asset.expiryDate)}</div>
          </div>
        )}
        
        {asset.remainingDays !== undefined && (
          <div className="info-item">
            <div className="info-label">Remaining Days</div>
            <div className="info-value">{asset.remainingDays} days</div>
          </div>
        )}
        
        <div className="info-item">
          <div className="info-label">Owner</div>
          <div className="info-value address">{formatAddress(asset.owner.walletAddress)}</div>
        </div>
      </div>
    </div>
  );

  // Render the rewards tab
  const renderRewardsTab = () => {
    const currentDate = new Date();
    const pastWeek = Array.from({length: 7}, (_, i) => {
      const date = new Date();
      date.setDate(currentDate.getDate() - i);
      return date;
    }).reverse();

    // Generate some mock daily reward data if not provided
    const dailyRewards = asset.dailyRewards || {};
    const rewardsData = pastWeek.map(date => {
      const dateString = date.toISOString().split('T')[0];
      return {
        date: dateString,
        rewards: dailyRewards[dateString] || (Math.random() * 10).toFixed(2)
      };
    });

    // Calculate estimated earnings
    const estimatedDaily = (asset.value * (asset.currentApy / 100) / 365).toFixed(2);
    const estimatedMonthly = (asset.value * (asset.currentApy / 100) / 12).toFixed(2);
    const estimatedYearly = (asset.value * (asset.currentApy / 100)).toFixed(2);

    return (
      <div className="asset-rewards-tab">
        <div className="rewards-summary">
          <div className="summary-card">
            <div className="summary-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.59 13.41L13.42 20.58C13.2343 20.766 13.0137 20.9135 12.7709 21.0141C12.5281 21.1148 12.2678 21.1666 12.005 21.1666C11.7422 21.1666 11.4819 21.1148 11.2391 21.0141C10.9963 20.9135 10.7757 20.766 10.59 20.58L7 17V13H3L10.17 5.83C10.3557 5.64397 10.5763 5.49654 10.8191 5.39592C11.0619 5.2953 11.3222 5.24347 11.585 5.24347C11.8478 5.24347 12.1081 5.2953 12.3509 5.39592C12.5937 5.49654 12.8143 5.64397 13 5.83L20.59 13.41ZM6.5 10C7.32843 10 8 9.32843 8 8.5C8 7.67157 7.32843 7 6.5 7C5.67157 7 5 7.67157 5 8.5C5 9.32843 5.67157 10 6.5 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="summary-content">
              <div className="summary-value">{asset.totalRewards.toFixed(2)} AFI</div>
              <div className="summary-label">Total Rewards</div>
            </div>
          </div>
        </div>
        
        <div className="rewards-estimates">
          <h4>Estimated Earnings</h4>
          <div className="estimates-grid">
            <div className="estimate-item">
              <div className="estimate-label">Daily</div>
              <div className="estimate-value">{estimatedDaily} AFI</div>
            </div>
            
            <div className="estimate-item">
              <div className="estimate-label">Monthly</div>
              <div className="estimate-value">{estimatedMonthly} AFI</div>
            </div>
            
            <div className="estimate-item">
              <div className="estimate-label">Yearly</div>
              <div className="estimate-value">{estimatedYearly} AFI</div>
            </div>
          </div>
        </div>
        
        <div className="rewards-history">
          <h4>Recent Rewards</h4>
          <div className="rewards-table">
            <div className="rewards-table-header">
              <div className="rewards-date">Date</div>
              <div className="rewards-amount">Amount</div>
            </div>
            {rewardsData.map((day, index) => (
              <div key={index} className="rewards-table-row">
                <div className="rewards-date">{formatDate(day.date)}</div>
                <div className="rewards-amount">{day.rewards} AFI</div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="rewards-actions">
          <button className="btn">Claim Rewards</button>
          <button className="btn btn-secondary">View Transactions</button>
        </div>
      </div>
    );
  };

  // Render the settings tab
  const renderSettingsTab = () => (
    <div className="asset-settings-tab">
      <div className="settings-section">
        <h4>Device Name</h4>
        <div className="device-name-setting">
          <input 
            type="text" 
            value={asset.name}
            placeholder="Enter device name" 
            className="device-name-input"
          />
          <button className="btn">Update Name</button>
        </div>
      </div>
      
      <div className="settings-section">
        <h4>Notifications</h4>
        <div className="notification-setting">
          <div className="notification-option">
            <input type="checkbox" id="reward-notification" />
            <label htmlFor="reward-notification">Reward Notifications</label>
          </div>
          <div className="notification-description">
            Receive notifications when you earn rewards
          </div>
        </div>
        
        <div className="notification-setting">
          <div className="notification-option">
            <input type="checkbox" id="status-notification" />
            <label htmlFor="status-notification">Status Notifications</label>
          </div>
          <div className="notification-description">
            Receive notifications about device status changes
          </div>
        </div>
      </div>
      
      <div className="settings-section">
        <h4>Advanced Settings</h4>
        <div className="advanced-settings">
          <button className="btn btn-secondary">Reinvest Rewards</button>
          <button className="btn btn-secondary">Transfer Ownership</button>
          <button className="btn btn-danger">Deactivate Device</button>
        </div>
      </div>
    </div>
  );

  // Determine which tab content to render
  const renderTabContent = () => {
    switch (selectedTab) {
      case 'overview':
        return renderOverviewTab();
      case 'rewards':
        return renderRewardsTab();
      case 'settings':
        return renderSettingsTab();
      default:
        return renderOverviewTab();
    }
  };

  return (
    <div className="asset-details-page">
      <div className="container">
        <header className="asset-details-header">
          <button className="back-button" onClick={() => navigate('/portfolio')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Portfolio
          </button>
          
          <h1>Device <span>Details</span></h1>
        </header>
        
        {!wallet.connected ? (
          renderWalletDisconnected()
        ) : isLoading ? (
          <div className="asset-details-loading">
            <div className="spinner"></div>
            <p>Loading device details...</p>
          </div>
        ) : error ? (
          <div className="asset-details-error">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
              <path d="M12 8V12" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 16H12.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>{error}</p>
            <div className="asset-details-actions">
              <button className="btn" onClick={fetchAssetDetails}>Try Again</button>
              <button className="btn btn-secondary" onClick={() => navigate('/portfolio')}>
                Go to Portfolio
              </button>
            </div>
          </div>
        ) : !asset ? (
          <div className="asset-details-empty">
            <div className="empty-state-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 11H5C3.89543 11 3 11.8954 3 13V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V13C21 11.8954 20.1046 11 19 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 11V7C7 5.93913 7.42143 4.92172 8.17157 4.17157C8.92172 3.42143 9.93913 3 11 3H13C14.0609 3 15.0783 3.42143 15.8284 4.17157C16.5786 4.92172 17 5.93913 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="16" r="1" fill="currentColor"/>
              </svg>
            </div>
            <h3>Device Not Found</h3>
            <p>We couldn't find this device in your portfolio.</p>
            <button className="btn" onClick={() => navigate('/portfolio')}>
              Go to Portfolio
            </button>
          </div>
        ) : (
          <div className="asset-details-content">
            <div className="asset-card-large">
              <div className="asset-visualization">
                <div className="asset-badge">{asset.tier}</div>
                {asset.type === 'enterprise' ? (
                  <EnterpriseDeviceCluster />
                ) : (
                  <Device type={asset.type} />
                )}
                <h2 className="asset-name">{asset.name}</h2>
              </div>
              
              <div className="asset-tabs">
                <button 
                  className={`tab-btn ${selectedTab === 'overview' ? 'active' : ''}`}
                  onClick={() => setSelectedTab('overview')}
                >
                  Overview
                </button>
                <button 
                  className={`tab-btn ${selectedTab === 'rewards' ? 'active' : ''}`}
                  onClick={() => setSelectedTab('rewards')}
                >
                  Rewards
                </button>
                <button 
                  className={`tab-btn ${selectedTab === 'settings' ? 'active' : ''}`}
                  onClick={() => setSelectedTab('settings')}
                >
                  Settings
                </button>
              </div>
              
              <div className="asset-tab-content">
                {renderTabContent()}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetDetailsPage;
